import { sqliteService } from './SQLiteService';
import { networkManager } from './NetworkManager';
import * as supabaseApi from '../../api/supabase';

type SyncOperation = 'create' | 'update' | 'delete';
type SyncStatus = 'pending' | 'processing' | 'completed' | 'failed';

interface SyncItem {
  id: number;
  operation_type: SyncOperation;
  table_name: string;
  record_id: string;
  data: string | null;
  created_at: string;
  retry_count: number;
  last_error: string | null;
  priority: number;
  status: SyncStatus;
}

/**
 * SyncQueue - Manages offline operations and synchronization
 * Handles queuing, retry logic, and conflict resolution
 */
export class SyncQueue {
  private static instance: SyncQueue;
  private isProcessing = false;
  private maxRetries = 3;
  private retryDelays = [1000, 5000, 15000]; // Exponential backoff in ms

  private constructor() {
    this.setupNetworkListener();
  }

  static getInstance(): SyncQueue {
    if (!SyncQueue.instance) {
      SyncQueue.instance = new SyncQueue();
    }
    return SyncQueue.instance;
  }

  private setupNetworkListener(): void {
    networkManager.addSyncCallback(async () => {
      await this.processPendingItems();
    });
  }

  /**
   * Add an operation to the sync queue
   */
  async addToQueue(
    operation: SyncOperation,
    tableName: string,
    recordId: string,
    data?: any,
    priority = 5
  ): Promise<void> {
    try {
      await sqliteService.addToSyncQueue(operation, tableName, recordId, data, priority);
      console.log(`📝 Added ${operation} operation to sync queue: ${tableName}:${recordId}`);
      
      // If online, try to process immediately
      if (networkManager.getConnectionStatus()) {
        setTimeout(() => this.processPendingItems(), 100);
      }
    } catch (error) {
      console.error('Error adding to sync queue:', error);
      
      // If database is not initialized, store operation in memory temporarily
      if (error instanceof Error && error.message.includes('Database not initialized')) {
        console.warn('📝 Database not ready, skipping sync queue for now. This is expected during startup.');
        return; // Don't throw error, just skip
      }
      
      throw error;
    }
  }

  /**
   * Process all pending sync items
   */
  async processPendingItems(): Promise<void> {
    if (this.isProcessing || !networkManager.getConnectionStatus()) {
      return;
    }

    this.isProcessing = true;

    try {
      console.log('🔄 Processing pending sync items...');
      
      const pendingItems = await sqliteService.getPendingSyncItems(50);
      console.log(`📋 Found ${pendingItems.length} pending sync items`);

      for (const item of pendingItems) {
        await this.processItem(item);
      }

      console.log('✅ Finished processing sync queue');
    } catch (error) {
      console.error('❌ Error processing sync queue:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Process a single sync item
   */
  private async processItem(item: SyncItem): Promise<void> {
    try {
      // Mark as processing
      await sqliteService.updateSyncItemStatus(item.id, 'processing');

      // Parse data if available
      const data = item.data ? JSON.parse(item.data) : null;

      // Execute the operation
      await this.executeOperation(item.operation_type, item.table_name, item.record_id, data);

      // Mark as completed
      await sqliteService.updateSyncItemStatus(item.id, 'completed');
      
      // Mark the original record as synced
      await sqliteService.markAsSynced(item.table_name, item.record_id);

      console.log(`✅ Synced ${item.operation_type} ${item.table_name}:${item.record_id}`);

    } catch (error: any) {
      console.error(`❌ Error syncing ${item.operation_type} ${item.table_name}:${item.record_id}:`, error);

      // Handle retry logic
      if (item.retry_count < this.maxRetries) {
        await sqliteService.updateSyncItemStatus(item.id, 'pending', error.message);
        
        // Schedule retry with exponential backoff
        const delay = this.retryDelays[Math.min(item.retry_count, this.retryDelays.length - 1)];
        setTimeout(() => {
          if (networkManager.getConnectionStatus()) {
            this.processPendingItems();
          }
        }, delay);
        
        console.log(`⏰ Scheduled retry ${item.retry_count + 1}/${this.maxRetries} in ${delay}ms`);
      } else {
        await sqliteService.updateSyncItemStatus(item.id, 'failed', error.message);
        console.error(`💀 Failed to sync after ${this.maxRetries} attempts: ${item.table_name}:${item.record_id}`);
      }
    }
  }

  /**
   * Execute the actual sync operation with Supabase
   */
  private async executeOperation(
    operation: SyncOperation,
    tableName: string,
    recordId: string,
    data: any
  ): Promise<void> {
    switch (operation) {
      case 'create':
        await this.handleCreate(tableName, recordId, data);
        break;
      case 'update':
        await this.handleUpdate(tableName, recordId, data);
        break;
      case 'delete':
        await this.handleDelete(tableName, recordId);
        break;
      default:
        throw new Error(`Unknown operation type: ${operation}`);
    }
  }

  /**
   * Handle create operations
   */
  private async handleCreate(tableName: string, recordId: string, data: any): Promise<void> {
    switch (tableName) {
      case 'vehicles':
        await supabaseApi.createVehicle(data);
        break;
      case 'drivers':
        await supabaseApi.createDriver(data);
        break;
      case 'vehicle_assignments':
        await supabaseApi.createVehicleAssignment(data);
        break;
      case 'vehicle_receiving_forms':
        await supabaseApi.createVehicleReceivingForm(data);
        break;
      case 'vehicle_deliveries':
        await supabaseApi.createVehicleDelivery(data);
        break;
      case 'vehicle_delivery_forms':
        await supabaseApi.createVehicleDelivery(data);
        break;
      case 'vehicle_damages':
        await supabaseApi.createVehicleDamage(data);
        break;
      case 'accounting_transactions':
        // Use createTransaction for accounting transactions
        await supabaseApi.createTransaction(data);
        break;
      default:
        throw new Error(`Unknown table for create operation: ${tableName}`);
    }
  }

  /**
   * Handle update operations
   */
  private async handleUpdate(tableName: string, recordId: string, data: any): Promise<void> {
    switch (tableName) {
      case 'vehicles':
        await supabaseApi.updateVehicle(recordId, data);
        break;
      case 'drivers':
        await supabaseApi.updateDriver(recordId, data);
        break;
      case 'vehicle_assignments':
        // Need to implement if not exists
        throw new Error(`Update operation not implemented for table: ${tableName}`);
      default:
        throw new Error(`Unknown table for update operation: ${tableName}`);
    }
  }

  /**
   * Handle delete operations
   */
  private async handleDelete(tableName: string, recordId: string): Promise<void> {
    switch (tableName) {
      case 'vehicles':
        await supabaseApi.deleteVehicle(recordId);
        break;
      case 'drivers':
        await supabaseApi.deleteDriver(recordId);
        break;
      case 'vehicle_assignments':
        await supabaseApi.deleteVehicleAssignment(recordId);
        break;
      case 'vehicle_damages':
        // Delete not implemented for damages - usually damages are kept for history
        console.warn(`Delete operation not implemented for vehicle_damages: ${recordId}`);
        break;
      default:
        throw new Error(`Unknown table for delete operation: ${tableName}`);
    }
  }

  /**
   * Get sync queue statistics
   */
  async getQueueStats(): Promise<{
    pending: number;
    processing: number;
    failed: number;
    completed: number;
  }> {
    const stats = await sqliteService.getDatabaseStats();
    
    // Get detailed queue stats
    const pending = await sqliteService.select('sync_queue', 'status = ?', ['pending']);
    const processing = await sqliteService.select('sync_queue', 'status = ?', ['processing']);
    const failed = await sqliteService.select('sync_queue', 'status = ?', ['failed']);
    const completed = await sqliteService.select('sync_queue', 'status = ? AND created_at > datetime("now", "-1 day")', ['completed']);

    return {
      pending: pending.length,
      processing: processing.length,
      failed: failed.length,
      completed: completed.length
    };
  }

  /**
   * Retry failed items
   */
  async retryFailedItems(): Promise<void> {
    await sqliteService.update(
      'sync_queue',
      { status: 'pending', retry_count: 0, last_error: null },
      'status = ?',
      ['failed']
    );

    if (networkManager.getConnectionStatus()) {
      await this.processPendingItems();
    }
  }

  /**
   * Clear completed sync items older than specified days
   */
  async clearOldCompletedItems(daysOld = 7): Promise<void> {
    await sqliteService.delete(
      'sync_queue',
      'status = ? AND created_at < datetime("now", "-' + daysOld + ' days")',
      ['completed']
    );
  }

  /**
   * Force sync all pending items (manual trigger)
   */
  async forceSyncAll(): Promise<void> {
    if (!networkManager.getConnectionStatus()) {
      throw new Error('Cannot sync while offline');
    }

    console.log('🚀 Force syncing all pending items...');
    await this.processPendingItems();
  }

  /**
   * Clear all pending items (emergency reset)
   */
  async clearAllPending(): Promise<void> {
    await sqliteService.delete('sync_queue', 'status IN (?, ?)', ['pending', 'failed']);
    console.log('🗑️ Cleared all pending sync items');
  }
}

// Export singleton instance
export const syncQueue = SyncQueue.getInstance();